/**
 * Report Page Entry Point
 * Main entry for the report submission page
 */

import Alpine from 'alpinejs'
import '../styles/main.css'

import { initSentry } from './config/sentry'

initSentry()

// Import stores
import { registerAuthStore } from './stores/auth.store'
import { registerToastsStore } from './stores/toasts.store'

// Import components
import { registerModalComponents } from './components/modal'
import { registerToastComponents } from './components/toast'
import { registerMapComponents } from './components/map'

// Import services
import { createReport, type CreateReportData } from './services/reports'
import { uploadImage, compressImage, createPreviewUrl, revokePreviewUrl } from './services/storage'
import type { GeoLocation, AnimalType, Condition } from './types'
import { escapeHtml, hasDangerousContent } from './utils/sanitize'
import { L } from './lib/leaflet'

// ============================================================================
// Report Form Component
// ============================================================================

interface ReportFormData {
  // Form state
  step: number
  loading: boolean
  error: string | null
  success: boolean
  showMap: boolean
  showPermissionHelp: boolean
  consent: boolean

  // Form data
  animalType: AnimalType | null
  condition: Condition
  description: string
  location: GeoLocation | null
  address: string
  country: string
  city: string
  imageFile: File | null
  imagePreview: string | null
  reporterName: string
  reporterEmail: string
  reporterPhone: string

  // Mini map
  miniMap: L.Map | null

  // Computed properties
  readonly completedSteps: number
  readonly canSubmit: boolean

  // Methods
  init(): void
  nextStep(): void
  prevStep(): void
  selectAnimalType(type: AnimalType): void
  selectCondition(condition: Condition): void
  handleImageSelect(event: Event): Promise<void>
  removeImage(): void
  setLocation(location: GeoLocation, address: string): void
  getGPSLocation(): Promise<void>
  clearLocation(): void
  initMiniMap(): void
  dismissPermissionHelp(): void
  validateStep(): boolean
  submit(): Promise<void>
  reset(): void
}

function reportForm(): ReportFormData {
  return {
    step: 1,
    loading: false,
    error: null,
    success: false,
    showMap: false,
    showPermissionHelp: false,
    consent: false,

    animalType: null,
    condition: 'unknown',
    description: '',
    location: null,
    address: '',
    country: '',
    city: '',
    imageFile: null,
    imagePreview: null,
    reporterName: '',
    reporterEmail: '',
    reporterPhone: '',
    miniMap: null,

    // Computed property: count of completed required steps
    get completedSteps(): number {
      let count = 0
      if (this.imagePreview) {
        count++
      }
      if (this.location) {
        count++
      }
      if (this.animalType) {
        count++
      }
      return count
    },

    // Computed property: can submit form
    get canSubmit(): boolean {
      return (
        this.imagePreview !== null &&
        this.location !== null &&
        this.animalType !== null &&
        this.consent
      )
    },

    init(): void {
      // Initialize stores
      const authStore = Alpine.store('auth') as { init(): void }
      authStore.init()
    },

    nextStep(): void {
      if (this.validateStep()) {
        this.step++
        this.error = null
      }
    },

    prevStep(): void {
      if (this.step > 1) {
        this.step--
        this.error = null
      }
    },

    selectAnimalType(type: AnimalType): void {
      this.animalType = type
    },

    selectCondition(condition: Condition): void {
      this.condition = condition
    },

    async handleImageSelect(event: Event): Promise<void> {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]

      if (!file) {
        return
      }

      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        this.error = 'Please upload a valid image (JPEG, PNG, GIF, or WebP)'
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        this.error = 'Image must be less than 10MB'
        return
      }

      try {
        // Compress image before preview
        const compressed = await compressImage(file, 1920, 1080, 0.8)
        this.imageFile = compressed

        // Create preview URL
        if (this.imagePreview) {
          revokePreviewUrl(this.imagePreview)
        }
        this.imagePreview = createPreviewUrl(compressed)
        this.error = null
      } catch (err) {
        this.error = 'Failed to process image'
        console.error('Image processing error:', err)
      }
    },

    removeImage(): void {
      if (this.imagePreview) {
        revokePreviewUrl(this.imagePreview)
      }
      this.imageFile = null
      this.imagePreview = null
    },

    setLocation(location: GeoLocation, address: string): void {
      this.location = location
      this.address = address
      this.showMap = false

      // Extract country and city from address if possible
      const parts = address.split(', ')
      if (parts.length >= 2) {
        this.country = parts[parts.length - 1] || ''
        this.city = parts.length >= 3 ? parts[parts.length - 3] || '' : parts[0] || ''
      }

      // Initialize mini map after DOM updates
      setTimeout(() => {
        this.initMiniMap()
      }, 150)
    },

    async getGPSLocation(): Promise<void> {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime safety for older browsers
      if (!navigator.geolocation) {
        this.error = 'Geolocation is not supported by your browser'
        return
      }

      this.loading = true
      this.error = null

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          })
        })

        const loc: GeoLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }

        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${String(loc.latitude)}&lon=${String(loc.longitude)}`
          )
          const data = (await response.json()) as { display_name?: string }
          this.setLocation(
            loc,
            data.display_name ?? `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`
          )
        } catch {
          this.setLocation(loc, `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`)
        }
      } catch (err) {
        const geoError = err as GeolocationPositionError
        if (geoError.code === 1) {
          this.showPermissionHelp = true
        } else if (geoError.code === 2) {
          this.error = 'Location unavailable. Please try again or pick on map.'
        } else {
          this.error = 'Failed to get your location. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    dismissPermissionHelp(): void {
      this.showPermissionHelp = false
    },

    clearLocation(): void {
      if (this.miniMap) {
        this.miniMap.remove()
        this.miniMap = null
      }
      this.location = null
      this.address = ''
      this.country = ''
      this.city = ''
    },

    initMiniMap(): void {
      if (!this.location) {
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const refs = (this as any).$refs as Record<string, HTMLElement> | undefined
      const container = refs?.miniMapContainer
      if (!container) {
        return
      }

      // Clean up existing mini map
      if (this.miniMap) {
        this.miniMap.remove()
        this.miniMap = null
      }

      const { latitude, longitude } = this.location

      const map = L.map(container, {
        center: [latitude, longitude],
        zoom: 15,
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: false,
      })

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map)
      L.marker([latitude, longitude]).addTo(map)

      this.miniMap = map

      // Ensure tiles load after container is visible
      setTimeout(() => {
        map.invalidateSize()
      }, 100)
    },

    validateStep(): boolean {
      switch (this.step) {
        case 1:
          if (!this.animalType) {
            this.error = 'Please select an animal type'
            return false
          }
          return true

        case 2:
          if (!this.imageFile) {
            this.error = 'Please upload a photo of the animal'
            return false
          }
          return true

        case 3:
          if (!this.location) {
            this.error = 'Please select a location on the map'
            return false
          }
          return true

        case 4:
          // Validate description for dangerous content
          if (hasDangerousContent(this.description)) {
            this.error = 'Description contains invalid content'
            return false
          }
          return true

        default:
          return true
      }
    },

    async submit(): Promise<void> {
      if (!this.validateStep()) {
        return
      }
      if (!this.location || !this.imageFile) {
        return
      }

      this.loading = true
      this.error = null

      try {
        // Upload image first
        const uploadResult = await uploadImage(this.imageFile, {
          folder: 'reports',
          onProgress: (progress) => {
            console.warn(`Upload progress: ${progress.progress.toFixed(0)}%`)
          },
        })

        // Create report data - use old schema format
        const reportData: CreateReportData = {
          animalType: this.animalType ?? ('other' as AnimalType),
          description: escapeHtml(this.description),
          location: this.location,
          locationDetails: {
            displayName: this.address,
            city: this.city,
            country: this.country,
          },
          manualLocation: this.address,
          photoURL: uploadResult.url,
          photoURLs: [uploadResult.url],
          consent: this.consent,
        }

        // Submit report
        await createReport(reportData)

        // Show success
        this.success = true
        const toasts = Alpine.store('toasts') as { success(msg: string): void }
        toasts.success('Report submitted successfully! It will be reviewed by our team.')
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to submit report'
        const toasts = Alpine.store('toasts') as { error(msg: string): void }
        toasts.error(this.error)
      } finally {
        this.loading = false
      }
    },

    reset(): void {
      this.step = 1
      this.loading = false
      this.error = null
      this.success = false
      this.showMap = false
      this.showPermissionHelp = false
      this.consent = false
      this.animalType = null
      this.condition = 'unknown'
      this.description = ''
      if (this.miniMap) {
        this.miniMap.remove()
        this.miniMap = null
      }
      this.location = null
      this.address = ''
      this.country = ''
      this.city = ''
      this.removeImage()
      this.reporterName = ''
      this.reporterEmail = ''
      this.reporterPhone = ''
    },
  }
}

// ============================================================================
// Initialize Application
// ============================================================================

// Register stores
registerAuthStore()
registerToastsStore()

// Register components
registerModalComponents()
registerToastComponents()
registerMapComponents()

// Register page components
Alpine.data('reportForm', reportForm)

// Start Alpine
Alpine.start()
